import React, { useCallback } from 'react';
import compose from 'lodash/fp/compose';
import { useSelector } from 'react-redux';
import {
  withTranslation,
  withUserPreferences,
  withCustomRouter,
  withUserDefaultSpace,
  withPersonalInfo,
  withCurrency,
} from 'components/hoc';
import TransactionContentTemplate from '../../common/TransactionContentTemplate';
import { useListTransaction } from '../hooks/useListTransaction';
import { useFavoriteOrganisation } from 'hooks/useFavoriteOrganisation';
import { useReferenceCheckingAccount } from 'hooks/useReferenceCheckingAccount';
import { useListCreditCard } from 'hooks/useListCreditCard';
import { getTransactionItem, getDCIndicator } from '../utils';
import { getFullName } from 'components/utils';
import LottieLoader from 'components/common/LottieLoader';

const EXCHANGE_DIFFERENCE_CATEGORIES = ['expense', 'income'];

const getTransactionItemByLine = (transactionItems, lineNumber, dcIndicator) => {
  if (!Array.isArray(transactionItems)) {
    return {};
  }

  return (
    transactionItems.find((item) => Number(item.line) === Number(lineNumber)) ||
    transactionItems.find((item) => item.dc_indicator === dcIndicator) ||
    transactionItems[lineNumber - 1] ||
    {}
  );
};

const getAccountCategoryId = (transactionItem) =>
  transactionItem?.account_category || transactionItem?.account_category_details?.id;

const isExchangeDifferenceItem = (transactionItem) =>
  Number(transactionItem?.line) === 3 &&
  EXCHANGE_DIFFERENCE_CATEGORIES.includes(getAccountCategoryId(transactionItem));

const getAssetShiftItems = (transactionItems) => ({
  debitItem: getTransactionItemByLine(transactionItems, 1, 'D'),
  creditItem: getTransactionItemByLine(transactionItems, 2, 'C'),
  exchangeDifferenceItem: Array.isArray(transactionItems)
    ? transactionItems.find(isExchangeDifferenceItem)
    : {},
});

const AssetShift = (props) => {
  const {
    data,
    refreshTransactions,
    lastItemRef,
    loading,
    filters,
    setFilters,
    applyFilters,
  } = useListTransaction('asset_shift');

  const {
    fn,
    fd,
    trObj,
    userDefaultSpace,
    gotoCreatePath,
    currencyLocaleOptions,
  } = props;

  const persons = useSelector((state) => state.personalInfo.data);
  const favoriteOrganisation = useFavoriteOrganisation();
  const referenceCheckingAccount = useReferenceCheckingAccount();
  const creditCardOptions = useListCreditCard();

  const filterableColumns = [
    { columnId: 'person', columnDataType: 'text' },
    { columnId: 'description', columnDataType: 'text' },
    { columnId: 'date', columnDataType: 'date' },
    { columnId: 'partner', columnDataType: 'text' },
    { columnId: 'amount', columnDataType: 'number' },
    { columnId: 'amount_sc', columnDataType: 'number' },
    { columnId: 'currency', columnDataType: 'text' },
    { columnId: 'contract', columnDataType: 'text' },
    { columnId: 'dc_indicator', columnDataType: 'text' },
    { columnId: 'partner_org_ind', columnDataType: 'text' },
    { columnId: 'bpos_org_indicator', columnDataType: 'text' },
    { columnId: 'account_category', columnDataType: 'text' },
    { columnId: 'financial_account', columnDataType: 'text' },
    { columnId: 'offset_account_category', columnDataType: 'text' },
    { columnId: 'offset_account', columnDataType: 'text' },
    { columnId: 'position_link', columnDataType: 'text' },
    { columnId: 'reversal', columnDataType: 'text' },
    { columnId: 'reversed', columnDataType: 'text' },
    { columnId: 'reverse_doc_no', columnDataType: 'number' },
    { columnId: 'original_doc_no', columnDataType: 'number' },
    { columnId: 'year', columnDataType: 'number' },
    { columnId: 'period', columnDataType: 'text' },
    { columnId: 'measurement_unit', columnDataType: 'text' },
    { columnId: 'quantity', columnDataType: 'number' },
    { columnId: 'line', columnDataType: 'number' },
    { columnId: 'sp_currency', columnDataType: 'text' },
    { columnId: 'transaction', columnDataType: 'number' },
  ];

  const getPositionLinkName = useCallback(
    (transactionItem) => {
      if (!transactionItem) return '';
      const { position_link, financial_account } = transactionItem;

      const personMap = new Map(
        persons.map((person) => [
          Number(person.id),
          `${person.first_name} ${person.last_name}`,
        ]),
      );
      const orgMap = new Map(
        favoriteOrganisation.map((org) => [String(org.id), org.name]),
      );
      const accountMap = new Map(
        referenceCheckingAccount.flatMap((account) => [
          [String(account.id), account.account_no],
          [String(account.account_no), account.account_no],
        ]),
      );
      const creditCardMap = new Map(
        creditCardOptions.map((card) => [String(card.id), `${card.number}`]),
      );

      if (['receive', 'payable'].includes(financial_account)) {
        return (
          personMap.get(Number(position_link)) ||
          orgMap.get(String(position_link)) ||
          position_link
        );
      }

      if (financial_account === 'bank') {
        return accountMap.get(String(position_link)) || position_link;
      }

      if (financial_account === 'cash') {
        const matchedCurrency = currencyLocaleOptions.find(
          (currency) => String(currency.id) === String(position_link),
        );
        return matchedCurrency ? trObj(matchedCurrency) : position_link;
      }

      if (financial_account === 'credit') {
        return creditCardMap.get(String(position_link)) || position_link;
      }

      return position_link;
    },
    [
      persons,
      favoriteOrganisation,
      referenceCheckingAccount,
      creditCardOptions,
      currencyLocaleOptions,
      trObj,
    ],
  );

  const mapFields = () => {
    return data?.flatMap((transaction) => {
      const transactionItems = transaction.transaction_item || [];
      const { debitItem, creditItem, exchangeDifferenceItem } =
        getAssetShiftItems(transactionItems);

      const personTransactionItem =
        transactionItems.find((item) => item.person_details) || debitItem;

      return [
        { accountItem: debitItem, offsetItem: creditItem, fallbackLine: 1 },
        { accountItem: creditItem, offsetItem: debitItem, fallbackLine: 2 },
        { accountItem: exchangeDifferenceItem, offsetItem: null, fallbackLine: 3 },
      ]
        .filter(({ accountItem }) => accountItem && Object.keys(accountItem).length)
        .map(({ accountItem, offsetItem, fallbackLine }) => {
          const normalizedAccountItem = getTransactionItem(accountItem, fn);
          const line = accountItem.line || fallbackLine;

          return {
            ...transaction,
            ...normalizedAccountItem,
            _id: `${transaction.id}-${line}`,
            space_name: transaction.space,
            person: getFullName(
              personTransactionItem?.person_details || transaction.person_detail,
            ),
            description: transaction.description,
            date: fd(transaction.date),
            partner: transaction?.partner_detail?.name,
            amount: fn(accountItem?.amount),
            amount_sc: fn(accountItem?.amount_sc),
            sp_currency: userDefaultSpace.space_detail.currency,
            currency: normalizedAccountItem.currency,
            contract: transaction.contract_details?.description,
            dc_indicator: getDCIndicator(normalizedAccountItem.dc_indicator),
            account_category: trObj(accountItem?.account_category_details),
            financial_account: trObj(accountItem?.financial_account_details),
            offset_account_category: trObj(offsetItem?.account_category_details),
            offset_account: trObj(offsetItem?.financial_account_details),
            reversal: transaction.reversal ? '\u2713' : '\u2717',
            reversed: transaction.reversed ? '\u2713' : '\u2717',
            measurement_unit: trObj(normalizedAccountItem?.measurement_unit_detail),
            reverse_doc_no: transaction.reverse_doc_no,
            original_doc_no: transaction.original_doc_no,
            line,
            position_link: getPositionLinkName(accountItem),
            offset_position_link: getPositionLinkName(offsetItem),
          };
        });
    });
  };

  const tableProps = {
    frontendTableName: 'asset_shift',
    backendTableName: 'transaction',
  };

  if (loading) {
    return <LottieLoader />;
  }

  return (
    <TransactionContentTemplate
      {...tableProps}
      columns={[]}
      data={mapFields() || []}
      toggleForm={gotoCreatePath}
      hasBulkDelete={false}
      refreshTransactions={refreshTransactions}
      filterableColumns={filterableColumns}
      filters={filters}
      setFilters={setFilters}
      applyFilters={applyFilters}
      lastItemRef={lastItemRef}
    />
  );
};

export default compose(
  withTranslation,
  withUserPreferences,
  withCustomRouter,
  withUserDefaultSpace,
  withPersonalInfo,
  withCurrency,
)(AssetShift);
